// ComposedExperienceSlide — the runtime shell for Kula Create composed
// experiences (Workshop, Phase 1). Renders an ordered list of capturing
// primitive BLOCKS through per-block themed components, threads the theme,
// aggregates ONE composite capture record for the whole experience, and fires
// the shared <ExperienceClose> with block-scoped {id.token} slots.
//
// Standard themed tier only (no flagship/Tier-A polish — that is the polish
// track). Blocks run top-to-bottom, always, in order (Phase 1 has no logic
// layer). A `reveal` stays locked until its referenced actionable block is
// committed — the commit-before-reveal invariant made physical.
//
// The capture logic + scoring live in lib/scenario-builder/composition/capture.ts
// (pure, unit-tested); this file is presentation + state only.

import { useState } from 'react';
import { motion } from 'framer-motion';
import type { ComposedExperienceSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type {
  ChoiceBlock,
  CommitBoxBlock,
  CompositionBlock,
  ConfidenceBlock,
  FlagInTextBlock,
  PerspectiveLensBlock,
  RateSliderBlock,
  RevealBlock,
  SelectEvidenceBlock,
  SortZonesBlock,
  StimulusBlock,
} from '../../../../../lib/scenario-builder/composition/types';
import type { Answers, BlockAnswer } from '../../../../../lib/scenario-builder/composition/capture';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { springs } from '../../../../ui/slide-animations';
import { useComposedExperience } from './useComposedExperience';
import { ComposedExperienceObsidian } from './ComposedExperienceObsidian';
import { ComposedExperienceAurora } from './ComposedExperienceAurora';
import { ComposedExperienceHyperpop } from './ComposedExperienceHyperpop';
import { ComposedExperienceTerra } from './ComposedExperienceTerra';
import { ComposedExperienceNeonOracle } from './ComposedExperienceNeonOracle';
import { ComposedExperienceRisograph } from './ComposedExperienceRisograph';

interface Props {
  data: ComposedExperienceSlideData;
  theme: Theme;
  isEditing?: boolean;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

// Dispatcher: a composed experience whose resolved theme is a Tier-A world
// (obsidian, so far) renders through that world's bespoke skin; everything else
// gets the standard themed tier. Each branch is its own component, so the
// shared capture hook is called unconditionally within whichever one mounts.
export function ComposedExperienceSlide(props: Props) {
  switch (props.theme.id) {
    case 'obsidian':
      return <ComposedExperienceObsidian {...props} />;
    case 'aurora':
      return <ComposedExperienceAurora {...props} />;
    case 'hyperpop':
      return <ComposedExperienceHyperpop {...props} />;
    case 'terra':
      return <ComposedExperienceTerra {...props} />;
    case 'neon-oracle':
      return <ComposedExperienceNeonOracle {...props} />;
    case 'risograph':
      return <ComposedExperienceRisograph {...props} />;
    default:
      return <ComposedExperienceStandard {...props} />;
  }
}

function ComposedExperienceStandard({
  data,
  theme,
  isEditing,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  const { blocks, answers, setAnswer, complete, values, rollup } = useComposedExperience({
    data,
    isEditing,
    onActivityResponse,
    onComplete,
    savedResponse,
  });

  const kicker: React.CSSProperties = {
    fontFamily: theme.fonts.code || theme.fonts.body,
    fontSize: 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: theme.colors.accent || theme.colors.primary,
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px 96px' }}>
        {data.intent && <div style={{ ...kicker, marginBottom: 10 }}>{data.intent}</div>}
        <h1
          style={{
            fontFamily: theme.fonts.heading,
            fontWeight: 700,
            fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
            lineHeight: 1.1,
            margin: '0 0 6px',
            color: theme.colors.text,
          }}
        >
          {data.title}
        </h1>
        {data.subtitle && (
          <p style={{ color: theme.colors.textMuted, fontSize: 15, margin: '0 0 24px' }}>{data.subtitle}</p>
        )}

        <div className="space-y-5">
          {blocks.map((block) => (
            <BlockView
              key={block.id}
              block={block}
              theme={theme}
              answer={answers[block.id]}
              answers={answers}
              onAnswer={(a) => setAnswer(block.id, a)}
            />
          ))}
        </div>

        {complete && data.conclusion && (
          <div className="mt-6">
            <ExperienceClose
              conclusion={data.conclusion}
              theme={theme}
              values={values}
              outcome={data.scoring.mode === 'none' ? undefined : String(Math.round(rollup * 100))}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-block dispatch
// ---------------------------------------------------------------------------

interface BlockProps {
  block: CompositionBlock;
  theme: Theme;
  answer: BlockAnswer | undefined;
  answers: Answers;
  onAnswer: (a: BlockAnswer) => void;
}

function BlockView({ block, theme, answer, answers, onAnswer }: BlockProps) {
  switch (block.type) {
    case 'stimulus':
      return <StimulusView block={block} theme={theme} />;
    case 'choice':
      return <ChoiceView block={block} theme={theme} answer={answer} onAnswer={onAnswer} />;
    case 'rate-slider':
      return <RateSliderView block={block} theme={theme} answer={answer} onAnswer={onAnswer} />;
    case 'sort-zones':
      return <SortZonesView block={block} theme={theme} answer={answer} onAnswer={onAnswer} />;
    case 'select-evidence':
      return <SelectEvidenceView block={block} theme={theme} answer={answer} onAnswer={onAnswer} />;
    case 'reveal':
      return <RevealView block={block} theme={theme} answers={answers} />;
    case 'commit-box':
      return <CommitBoxView block={block} theme={theme} answer={answer} onAnswer={onAnswer} />;
    case 'confidence':
      return <ConfidenceView block={block} theme={theme} answer={answer} onAnswer={onAnswer} />;
    case 'flag-in-text':
      return <FlagInTextView block={block} theme={theme} answer={answer} onAnswer={onAnswer} />;
    case 'perspective-lens':
      return <PerspectiveLensView block={block} theme={theme} answer={answer} onAnswer={onAnswer} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Shared card frame
// ---------------------------------------------------------------------------

function Card({ theme, children }: { theme: Theme; children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.gentle as object}
      className="rounded-xl border p-5"
      style={{ background: theme.colors.surface, borderColor: `${theme.colors.textMuted}22` }}
    >
      {children}
    </motion.div>
  );
}

function Prompt({ theme, children }: { theme: Theme; children: React.ReactNode }) {
  return (
    <p className="font-semibold mb-3" style={{ color: theme.colors.text, fontFamily: theme.fonts.heading }}>
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// stimulus
// ---------------------------------------------------------------------------

function StimulusView({ block, theme }: { block: StimulusBlock; theme: Theme }) {
  return (
    <Card theme={theme}>
      {block.content.heading && (
        <p className="text-xs uppercase tracking-wider font-bold mb-1.5" style={{ color: theme.colors.accent || theme.colors.primary }}>
          {block.content.heading}
        </p>
      )}
      <p className="text-base leading-relaxed" style={{ color: theme.colors.text }}>
        {block.content.body}
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// choice
// ---------------------------------------------------------------------------

function ChoiceView({ block, theme, answer, onAnswer }: { block: ChoiceBlock; theme: Theme; answer?: BlockAnswer; onAnswer: (a: BlockAnswer) => void }) {
  const chosen = (answer as { optionId: string } | undefined)?.optionId;
  const accent = theme.colors.accent || theme.colors.primary;
  const chosenOpt = block.content.options.find((o) => o.id === chosen);
  return (
    <Card theme={theme}>
      <Prompt theme={theme}>{block.content.prompt}</Prompt>
      <div className="space-y-2">
        {block.content.options.map((opt) => {
          const isChosen = opt.id === chosen;
          return (
            <button
              key={opt.id}
              disabled={!!chosen}
              onClick={() => onAnswer({ optionId: opt.id })}
              className="w-full text-left rounded-lg border px-4 py-3 text-sm transition-colors"
              style={{
                borderColor: isChosen ? accent : `${theme.colors.textMuted}33`,
                background: isChosen ? `${accent}14` : theme.colors.background,
                color: theme.colors.text,
                cursor: chosen ? 'default' : 'pointer',
                opacity: chosen && !isChosen ? 0.55 : 1,
              }}
            >
              {opt.text}
            </button>
          );
        })}
      </div>
      {chosenOpt?.feedback && (
        <p className="mt-3 text-sm rounded-lg px-3 py-2" style={{ background: `${accent}10`, color: theme.colors.textMuted }}>
          {chosenOpt.feedback}
        </p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// rate-slider
// ---------------------------------------------------------------------------

function RateSliderView({ block, theme, answer, onAnswer }: { block: RateSliderBlock; theme: Theme; answer?: BlockAnswer; onAnswer: (a: BlockAnswer) => void }) {
  const { min, max, step } = block.structure;
  const committed = (answer as { value: number } | undefined)?.value;
  const [pos, setPos] = useState<number>(committed ?? Math.round((min + max) / 2));
  const accent = theme.colors.accent || theme.colors.primary;
  const locked = committed !== undefined;
  return (
    <Card theme={theme}>
      <Prompt theme={theme}>{block.content.prompt}</Prompt>
      <input
        type="range"
        min={min}
        max={max}
        step={step ?? 1}
        value={locked ? committed : pos}
        disabled={locked}
        onChange={(e) => setPos(Number(e.target.value))}
        className="w-full"
        style={{ accentColor: accent }}
      />
      <div className="flex justify-between text-xs mt-1" style={{ color: theme.colors.textMuted }}>
        <span>{block.content.minLabel ?? min}</span>
        <span style={{ color: accent, fontWeight: 700 }}>{locked ? committed : pos}</span>
        <span>{block.content.maxLabel ?? max}</span>
      </div>
      {!locked && (
        <button
          onClick={() => onAnswer({ value: pos })}
          className="mt-3 rounded-lg px-4 py-2 text-sm font-semibold"
          style={{ background: accent, color: '#fff' }}
        >
          Lock in
        </button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// sort-zones — click-to-assign (drag-free for a robust v1)
// ---------------------------------------------------------------------------

function SortZonesView({ block, theme, answer, onAnswer }: { block: SortZonesBlock; theme: Theme; answer?: BlockAnswer; onAnswer: (a: BlockAnswer) => void }) {
  const committed = (answer as { placement: Record<string, string> } | undefined)?.placement;
  const [placement, setPlacement] = useState<Record<string, string>>(committed ?? {});
  const accent = theme.colors.accent || theme.colors.primary;
  const locked = committed !== undefined;
  const allPlaced = block.content.items.every((it) => placement[it.id]);
  return (
    <Card theme={theme}>
      <Prompt theme={theme}>{block.content.prompt}</Prompt>
      <div className="space-y-3">
        {block.content.items.map((it) => (
          <div key={it.id}>
            <p className="text-sm mb-1.5" style={{ color: theme.colors.text }}>{it.text}</p>
            <div className="flex flex-wrap gap-2">
              {block.content.zones.map((z) => {
                const active = placement[it.id] === z.id;
                return (
                  <button
                    key={z.id}
                    disabled={locked}
                    onClick={() => setPlacement((p) => ({ ...p, [it.id]: z.id }))}
                    className="rounded-full border px-3 py-1 text-xs"
                    style={{
                      borderColor: active ? accent : `${theme.colors.textMuted}33`,
                      background: active ? `${accent}18` : theme.colors.background,
                      color: theme.colors.text,
                      cursor: locked ? 'default' : 'pointer',
                    }}
                  >
                    {z.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {!locked && (
        <button
          disabled={!allPlaced}
          onClick={() => onAnswer({ placement })}
          className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold"
          style={{ background: accent, color: '#fff', opacity: allPlaced ? 1 : 0.4, cursor: allPlaced ? 'pointer' : 'not-allowed' }}
        >
          Submit
        </button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// select-evidence
// ---------------------------------------------------------------------------

function SelectEvidenceView({ block, theme, answer, onAnswer }: { block: SelectEvidenceBlock; theme: Theme; answer?: BlockAnswer; onAnswer: (a: BlockAnswer) => void }) {
  const committed = (answer as { selected: string[] } | undefined)?.selected;
  const [selected, setSelected] = useState<string[]>(committed ?? []);
  const accent = theme.colors.accent || theme.colors.primary;
  const locked = committed !== undefined;
  const max = block.structure.maxSelections;
  const atMax = max !== undefined && selected.length >= max;
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : atMax ? s : [...s, id]));
  return (
    <Card theme={theme}>
      {block.content.claim && (
        <p className="text-sm italic mb-2" style={{ color: theme.colors.textMuted }}>{block.content.claim}</p>
      )}
      <Prompt theme={theme}>{block.content.prompt}</Prompt>
      <div className="space-y-2">
        {block.content.items.map((it) => {
          const on = selected.includes(it.id);
          return (
            <button
              key={it.id}
              disabled={locked || (!on && atMax)}
              onClick={() => toggle(it.id)}
              className="w-full text-left rounded-lg border px-4 py-3 text-sm flex items-start gap-3"
              style={{
                borderColor: on ? accent : `${theme.colors.textMuted}33`,
                background: on ? `${accent}14` : theme.colors.background,
                color: theme.colors.text,
                cursor: locked ? 'default' : 'pointer',
                opacity: !on && atMax && !locked ? 0.5 : 1,
              }}
            >
              <span
                className="mt-0.5 inline-block h-4 w-4 rounded border flex-shrink-0"
                style={{ borderColor: accent, background: on ? accent : 'transparent' }}
              />
              <span>{it.text}</span>
            </button>
          );
        })}
      </div>
      {max !== undefined && !locked && (
        <p className="mt-2 text-xs" style={{ color: theme.colors.textMuted }}>{selected.length}/{max} selected</p>
      )}
      {!locked && (
        <button
          disabled={selected.length === 0}
          onClick={() => onAnswer({ selected })}
          className="mt-3 rounded-lg px-4 py-2 text-sm font-semibold"
          style={{ background: accent, color: '#fff', opacity: selected.length ? 1 : 0.4, cursor: selected.length ? 'pointer' : 'not-allowed' }}
        >
          Submit
        </button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// reveal — locked until its referenced actionable block is committed
// ---------------------------------------------------------------------------

function RevealView({ block, theme, answers }: { block: RevealBlock; theme: Theme; answers: Answers }) {
  const unlocked = answers[block.ref] !== undefined;
  const accent = theme.colors.accent || theme.colors.primary;
  if (!unlocked) {
    return (
      <div
        className="rounded-xl border border-dashed px-5 py-4 text-sm text-center"
        style={{ borderColor: `${theme.colors.textMuted}44`, color: theme.colors.textMuted }}
      >
        Commit above to reveal the expert view.
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.gentle as object}
      className="rounded-xl border-l-[5px] p-5"
      style={{ background: theme.colors.surface, borderColor: accent }}
    >
      <p className="text-xs uppercase tracking-wider font-bold mb-1.5" style={{ color: accent }}>
        {block.content.heading || 'The expert view'}
      </p>
      <p className="text-base leading-relaxed" style={{ color: theme.colors.text }}>
        {block.content.body}
      </p>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// commit-box (articulation)
// ---------------------------------------------------------------------------

function CommitBoxView({ block, theme, answer, onAnswer }: { block: CommitBoxBlock; theme: Theme; answer?: BlockAnswer; onAnswer: (a: BlockAnswer) => void }) {
  const committed = (answer as { text: string } | undefined)?.text;
  const [text, setText] = useState(committed ?? '');
  const accent = theme.colors.accent || theme.colors.primary;
  const locked = committed !== undefined;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const minW = block.structure.minWords ?? 1;
  const ok = words >= minW;
  return (
    <Card theme={theme}>
      <Prompt theme={theme}>{block.content.prompt}</Prompt>
      {locked ? (
        <p className="text-sm rounded-lg px-3 py-2" style={{ background: `${accent}10`, color: theme.colors.text }}>{committed}</p>
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={block.content.placeholder}
            rows={3}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            style={{ background: theme.colors.background, borderColor: `${theme.colors.textMuted}33`, color: theme.colors.text, fontFamily: theme.fonts.body }}
          />
          <button
            disabled={!ok}
            onClick={() => onAnswer({ text: text.trim() })}
            className="mt-2 rounded-lg px-4 py-2 text-sm font-semibold"
            style={{ background: accent, color: '#fff', opacity: ok ? 1 : 0.4, cursor: ok ? 'pointer' : 'not-allowed' }}
          >
            Commit
          </button>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// confidence (Wave 2) — a self-report slider; never graded
// ---------------------------------------------------------------------------

function ConfidenceView({ block, theme, answer, onAnswer }: { block: ConfidenceBlock; theme: Theme; answer?: BlockAnswer; onAnswer: (a: BlockAnswer) => void }) {
  const { min, max, step } = block.structure;
  const committed = (answer as { value: number } | undefined)?.value;
  const [pos, setPos] = useState<number>(committed ?? Math.round((min + max) / 2));
  const accent = theme.colors.accent || theme.colors.primary;
  const locked = committed !== undefined;
  return (
    <Card theme={theme}>
      <Prompt theme={theme}>{block.content.prompt}</Prompt>
      <input
        type="range"
        min={min}
        max={max}
        step={step ?? 1}
        value={locked ? committed : pos}
        disabled={locked}
        onChange={(e) => setPos(Number(e.target.value))}
        className="w-full"
        style={{ accentColor: accent }}
      />
      <div className="flex justify-between text-xs mt-1" style={{ color: theme.colors.textMuted }}>
        <span>{block.content.minLabel ?? min}</span>
        <span style={{ color: accent, fontWeight: 700 }}>{locked ? committed : pos}</span>
        <span>{block.content.maxLabel ?? max}</span>
      </div>
      {!locked && (
        <button onClick={() => onAnswer({ value: pos })} className="mt-3 rounded-lg px-4 py-2 text-sm font-semibold" style={{ background: accent, color: '#fff' }}>
          Lock in
        </button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// flag-in-text (Wave 2) — click the wrong/weak spans in a passage
// ---------------------------------------------------------------------------

function FlagInTextView({ block, theme, answer, onAnswer }: { block: FlagInTextBlock; theme: Theme; answer?: BlockAnswer; onAnswer: (a: BlockAnswer) => void }) {
  const committed = (answer as { flagged: string[] } | undefined)?.flagged;
  const [flagged, setFlagged] = useState<string[]>(committed ?? []);
  const accent = theme.colors.accent || theme.colors.primary;
  const locked = committed !== undefined;
  const toggle = (id: string) => setFlagged((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  return (
    <Card theme={theme}>
      <Prompt theme={theme}>{block.content.prompt}</Prompt>
      <p className="text-base leading-loose" style={{ color: theme.colors.text }}>
        {block.content.segments.map((seg) => {
          const on = flagged.includes(seg.id);
          const wasRight = locked && seg.flagged === true;
          const wasWrong = locked && on && seg.flagged !== true;
          return (
            <span
              key={seg.id}
              onClick={() => !locked && toggle(seg.id)}
              style={{
                cursor: locked ? 'default' : 'pointer',
                background: on ? `${accent}26` : 'transparent',
                borderBottom: locked ? `2px solid ${wasRight ? '#2e7d32' : wasWrong ? accent : 'transparent'}` : on ? `2px solid ${accent}` : '2px solid transparent',
                borderRadius: 2,
                padding: '0 1px',
              }}
            >
              {seg.text}{' '}
            </span>
          );
        })}
      </p>
      {!locked && (
        <button
          disabled={flagged.length === 0}
          onClick={() => onAnswer({ flagged })}
          className="mt-3 rounded-lg px-4 py-2 text-sm font-semibold"
          style={{ background: accent, color: '#fff', opacity: flagged.length ? 1 : 0.4, cursor: flagged.length ? 'pointer' : 'not-allowed' }}
        >
          Submit ({flagged.length} flagged)
        </button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// perspective-lens (Wave 2) — a short take per named lens (articulation)
// ---------------------------------------------------------------------------

function PerspectiveLensView({ block, theme, answer, onAnswer }: { block: PerspectiveLensBlock; theme: Theme; answer?: BlockAnswer; onAnswer: (a: BlockAnswer) => void }) {
  const committed = (answer as { takes: Record<string, string> } | undefined)?.takes;
  const [takes, setTakes] = useState<Record<string, string>>(committed ?? {});
  const accent = theme.colors.accent || theme.colors.primary;
  const locked = committed !== undefined;
  const allFilled = block.content.lenses.every((l) => (takes[l.id] ?? '').trim().length > 0);
  return (
    <Card theme={theme}>
      <Prompt theme={theme}>{block.content.prompt}</Prompt>
      <div className="space-y-3">
        {block.content.lenses.map((lens) => (
          <div key={lens.id}>
            <p className="text-sm font-semibold" style={{ color: accent }}>{lens.label}</p>
            {lens.hint && <p className="text-xs mb-1" style={{ color: theme.colors.textMuted }}>{lens.hint}</p>}
            {locked ? (
              <p className="text-sm rounded-lg px-3 py-2" style={{ background: `${accent}10`, color: theme.colors.text }}>{takes[lens.id]}</p>
            ) : (
              <textarea
                value={takes[lens.id] ?? ''}
                onChange={(e) => setTakes((t) => ({ ...t, [lens.id]: e.target.value }))}
                rows={2}
                className="w-full rounded-lg border px-3 py-2 text-sm"
                style={{ background: theme.colors.background, borderColor: `${theme.colors.textMuted}33`, color: theme.colors.text, fontFamily: theme.fonts.body }}
              />
            )}
          </div>
        ))}
      </div>
      {!locked && (
        <button
          disabled={!allFilled}
          onClick={() => onAnswer({ takes })}
          className="mt-3 rounded-lg px-4 py-2 text-sm font-semibold"
          style={{ background: accent, color: '#fff', opacity: allFilled ? 1 : 0.4, cursor: allFilled ? 'pointer' : 'not-allowed' }}
        >
          Save
        </button>
      )}
    </Card>
  );
}
